import { useState, useEffect, useCallback, useRef } from 'react';
import { getSupabase } from '@/lib/supabase';
import { useAuthReady } from '@/hooks/useAuthReady';
import { useRealtimePing } from '@/hooks/useRealtimeTopic';
import type { GuestActionType } from '@/types/database';

export interface ActionFeedItem {
  id: string;
  booking_id: string;
  action_type: GuestActionType;
  guest_name: string;
  unit_name: string;
  property_name: string;
  latitude: number | null;
  longitude: number | null;
  is_read: boolean;
  created_at: string;
}

interface UseGuestActionsFeedReturn {
  actions: ActionFeedItem[];
  unreadCount: number;
  isLoading: boolean;
  markAllRead: () => Promise<void>;
  refetch: () => Promise<void>;
}

export function useGuestActionsFeed(
  landlordId: string | undefined,
): UseGuestActionsFeedReturn {
  const [actions, setActions] = useState<ActionFeedItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const prevIdsRef = useRef<Set<string>>(new Set());

  // The feed RPC only answers the logged-in host (security audit 2026-10-22),
  // so wait until the Clerk token is on the client — a first call without it
  // was refused and the bell stayed empty until the next 30 s poll.
  const { isReady } = useAuthReady();

  const fetchFeed = useCallback(async () => {
    if (!landlordId) {
      setIsLoading(false);
      return;
    }
    if (!isReady) return;

    try {
      const { data, error } = await getSupabase().rpc('get_guest_actions_feed', {
        p_landlord_id: landlordId,
      });

      if (error) throw error;

      const items = (data || []) as ActionFeedItem[];
      setActions(items);

      // Detect new actions and dispatch custom event
      const currentIds = new Set(items.map((a) => a.id));
      if (prevIdsRef.current.size > 0) {
        for (const item of items) {
          if (!prevIdsRef.current.has(item.id)) {
            window.dispatchEvent(
              new CustomEvent('guest-action-new', { detail: item }),
            );
          }
        }
      }
      prevIdsRef.current = currentIds;
    } catch (err) {
      console.error('Error fetching guest actions feed:', err);
    } finally {
      setIsLoading(false);
    }
  }, [landlordId, isReady]);

  // Initial fetch + polling fallback
  useEffect(() => {
    fetchFeed();
    const interval = setInterval(fetchFeed, 30000);
    return () => clearInterval(interval);
  }, [fetchFeed]);

  // Real-time subscription
  const fetchFeedRef = useRef(fetchFeed);
  useEffect(() => {
    fetchFeedRef.current = fetchFeed;
  }, [fetchFeed]);

  // Live: the database pings the host's private topic on every guest action.
  useRealtimePing('landlord', landlordId, (change) => {
    if (change.table === 'guest_actions') fetchFeedRef.current();
  });

  const unreadCount = actions.filter((a) => !a.is_read).length;

  const markAllRead = useCallback(async () => {
    if (!landlordId) return;

    try {
      await getSupabase().rpc('mark_actions_read', {
        p_landlord_id: landlordId,
      });
      // Update local state immediately
      setActions((prev) => prev.map((a) => ({ ...a, is_read: true })));
    } catch (err) {
      console.error('Error marking actions read:', err);
    }
  }, [landlordId]);

  return {
    actions,
    unreadCount,
    isLoading,
    markAllRead,
    refetch: fetchFeed,
  };
}
